import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Bell, Check, CheckCheck, Package, Truck, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import * as notificationApi from '@/services/notificationApi';
import * as authApi from '@/services/authApi';

// Notification sound
const notificationSound = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2telehmg1/NjIQAsicHQpJ4cLouu8JHCqWUEADqk0dNdEDxgktzS12xwLxJPl8/Mxc/I2JYWBFltsrHd1dvgixwBRWWQoOjv0uKQMwAtXm+X1e3d0qx0GgM5X4ed5vHZ3YElBy1ler3k6+HYkjkLIVN3m9vu6tyYRBEeTnGV3O7n26FPGhZIZ4m94+zg2plOGQ9CYoiy4uzc2ppSHQ0+YIWw4uva2ZtTHgs6Xoau4uvZ2JtUHwo4XYSs4uvY15xVIAg2XIKq4erX1p1WIQczW4Go4enW1Z5XIwYxWoCn4OnV1J9YJAQvWX+l3+jU05xZJQMuWH+k3+fT0p1aJgItV36j3ubS0Z5bJgEsV32i3eXR0J9cJwArVnyh3OTQz6BdKAApVXuf2+PPzqFeKAAoVHqe2uLOzaJfKgAnU3md2eHNzKNgKgAmUnib2ODMy6RhKwAlUXea19/LyqViLAAkUHaZ1t7KyaZjLQAjT3WY1d3JyKdkLgAiTnSX1NzIx6hlLwAhTXOW09vHxqlmMAAhTHKV0trGxapoMQAgS3GU0dnFxKtpMgAfSnCTz9jEw6xqMwAeSm+Sztf');

const Notifications = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const wsRef = useRef(null);

  useEffect(() => {
    fetchUser();
    fetchNotifications();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  useEffect(() => {
    if (user) {
      setupWebSocket();
    }
  }, [user]);

  const fetchUser = async () => {
    try {
      const userData = await authApi.getMe();
      setUser(userData);
    } catch (error) {
      console.error('Error fetching user:', error);
      navigate('/login');
    }
  };

  const fetchNotifications = async () => {
    setIsLoading(true);
    try {
      const data = await notificationApi.getNotifications();
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast.error('Failed to load notifications');
    } finally {
      setIsLoading(false);
    }
  };

  const setupWebSocket = () => {
    if (!user) return;
    
    wsRef.current = notificationApi.createNotificationWebSocket(
      user.user_id,
      (data) => {
        if (data.type === 'new_notification') {
          // Play sound
          notificationSound.play().catch(() => {});
          
          // Show browser notification if permitted
          if (Notification.permission === 'granted') {
            new Notification(data.notification.title, {
              body: data.notification.message,
              icon: '/favicon.ico'
            });
          }
          
          // Update state
          setNotifications(prev => [data.notification, ...prev]);
          setUnreadCount(prev => prev + 1);
          toast.info(data.notification.title);
        }
      },
      (error) => {
        console.error('WebSocket error:', error);
      }
    );
  };

  const handleMarkRead = async (notificationId) => {
    try {
      await notificationApi.markNotificationRead(notificationId);
      setNotifications(prev => 
        prev.map(n => n.notification_id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification read:', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationApi.markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Error marking all read:', error);
      toast.error('Failed to mark notifications as read');
    }
  };

  const requestNotificationPermission = async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        toast.success('Notifications enabled!');
      }
    }
  };

  useEffect(() => {
    requestNotificationPermission();
  }, []);

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'consignment_ready':
        return <Package className="w-5 h-5 text-blue-500" />;
      case 'consignment_requested':
        return <Bell className="w-5 h-5 text-orange-500" />;
      case 'consignment_shipped':
        return <Truck className="w-5 h-5 text-green-500" />;
      default:
        return <Bell className="w-5 h-5 text-gray-500" />;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-[#F9FAFB] hover:bg-white/10">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Bell className="w-6 h-6" />
              <h1 className="text-xl font-bold font-display">Notifications</h1>
              {unreadCount > 0 && (
                <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                  {unreadCount}
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={fetchNotifications} variant="secondary" className="h-10">
              <RefreshCw className="w-4 h-4" />
            </Button>
            {unreadCount > 0 && (
              <Button onClick={handleMarkAllRead} variant="secondary" className="h-10">
                <CheckCheck className="w-4 h-4 mr-2" />
                Mark All Read
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-8">
        {isLoading ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-[#2D5F4F] mx-auto" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border border-[#E5DFD0]">
            <Bell className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-[#5A5F6E]">No notifications yet</p>
          </div>
        ) : (
          <div className="space-y-4">
            {notifications.map((notification) => (
              <div
                key={notification.notification_id}
                className={`bg-white rounded-lg border-2 p-4 transition-all ${
                  notification.is_read 
                    ? 'border-[#E5DFD0]' 
                    : 'border-[#2D5F4F] bg-green-50'
                }`}
                data-testid={`notification-${notification.notification_id}`}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 mt-1">
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-[#1A1F2E]">{notification.title}</h3>
                      {!notification.is_read && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleMarkRead(notification.notification_id)}
                          className="h-8"
                          data-testid={`mark-read-${notification.notification_id}`}
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    <p className="text-sm text-[#5A5F6E] mt-1">{notification.message}</p>
                    
                    {/* Notification details */}
                    {notification.data && (
                      <div className="mt-3 p-3 bg-[#F5F1E8] rounded-lg text-sm">
                        <div className="grid grid-cols-2 gap-2">
                          {notification.document_barcode && (
                            <div>
                              <span className="text-[#5A5F6E]">Document:</span>{' '}
                              <span className="font-medium">{notification.document_barcode}</span>
                            </div>
                          )}
                          {notification.section_name && (
                            <div>
                              <span className="text-[#5A5F6E]">Section:</span>{' '}
                              <span className="font-medium">{notification.section_name}</span>
                            </div>
                          )}
                          {notification.data.vendor_name && (
                            <div>
                              <span className="text-[#5A5F6E]">Vendor:</span>{' '}
                              <span className="font-medium">{notification.data.vendor_name}</span>
                            </div>
                          )}
                          {notification.data.bundle_count !== undefined && (
                            <div>
                              <span className="text-[#5A5F6E]">Bundles:</span>{' '}
                              <span className="font-medium">{notification.data.bundle_count}</span>
                            </div>
                          )}
                          {notification.data.qty != null && notification.data.qty !== '' && (
                            <div>
                              <span className="text-[#5A5F6E]">Qty:</span>{' '}
                              <span className="font-medium">{notification.data.qty}</span>
                            </div>
                          )}
                          {notification.data.value != null && notification.data.value !== '' && (
                            <div>
                              <span className="text-[#5A5F6E]">Value:</span>{' '}
                              <span className="font-medium">₹{notification.data.value}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    <p className="text-xs text-[#5A5F6E] mt-2">
                      {formatDate(notification.created_at)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Notifications;
